/**
 * Touch gesture module for epub.js spine iframes, registered via
 * rendition.hooks.content.register(applyTouchGestures).
 *
 * Gestures:
 *   - Swipe: velocity-based page turn.
 *   - Long-press: on touch devices iOS handles native word selection;
 *     a selectionchange listener detects it and fires onLongPress so
 *     the highlight menu appears. On desktop, mouseup does the same.
 *   - Pinch zoom / double-tap reset.
 */

const SWIPE_MIN_DISTANCE_PX = 40;
const SWIPE_MIN_VELOCITY_PX_PER_MS = 0.3;
const SWIPE_VERTICAL_TOLERANCE_PX = 60;
const LONG_PRESS_MOVE_THRESHOLD_PX = 10;
const PINCH_MIN_SCALE = 1.0;
const PINCH_MAX_SCALE = 3.0;
const DOUBLE_TAP_WINDOW_MS = 300;
const DOUBLE_TAP_DISTANCE_PX = 30;

interface EpubJsContents {
  document: Document;
  window: Window;
}

interface RenditionLike {
  prev(): Promise<void>;
  next(): Promise<void>;
}

export interface TouchGestureCallbacks {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onLongPress?: (ev: { clientX: number; clientY: number; target: EventTarget | null }) => void;
}

export function createTouchGestureHandler(
  rendition: RenditionLike,
  cb: TouchGestureCallbacks = {},
) {
  return function applyTouchGestures(contents: EpubJsContents): void {
    const doc = contents.document;

    injectReaderStyles(doc);

    let swipeStart: { x: number; y: number; t: number } | null = null;
    let longPressStart: { x: number; y: number } | null = null;
    let pinchInitialDist: number | null = null;
    let pinchInitialScale = 1.0;
    let currentScale = 1.0;
    let lastTap: { t: number; x: number; y: number } | null = null;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const t = e.touches[0];
        swipeStart = { x: t.clientX, y: t.clientY, t: Date.now() };
        longPressStart = { x: t.clientX, y: t.clientY };
      } else if (e.touches.length === 2) {
        longPressStart = null;
        swipeStart = null;
        pinchInitialDist = distance(e.touches[0], e.touches[1]);
        pinchInitialScale = currentScale;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (longPressStart && e.touches.length === 1) {
        const t = e.touches[0];
        if (Math.hypot(t.clientX - longPressStart.x, t.clientY - longPressStart.y) > LONG_PRESS_MOVE_THRESHOLD_PX) {
          longPressStart = null;
        }
      }

      if (e.touches.length === 2 && pinchInitialDist != null) {
        const newDist = distance(e.touches[0], e.touches[1]);
        const scale = clamp(
          pinchInitialScale * (newDist / pinchInitialDist),
          PINCH_MIN_SCALE,
          PINCH_MAX_SCALE
        );
        applyScale(doc, scale);
        currentScale = scale;
        e.preventDefault();
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      longPressStart = null;

      if (pinchInitialDist != null && e.touches.length < 2) {
        pinchInitialDist = null;
      }

      if (swipeStart && e.changedTouches.length === 1) {
        const t = e.changedTouches[0];
        const dx = t.clientX - swipeStart.x;
        const dy = t.clientY - swipeStart.y;
        const dt = Math.max(1, Date.now() - swipeStart.t);
        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);
        const velocity = absDx / dt;

        if (
          absDx >= SWIPE_MIN_DISTANCE_PX &&
          velocity >= SWIPE_MIN_VELOCITY_PX_PER_MS &&
          absDy <= SWIPE_VERTICAL_TOLERANCE_PX
        ) {
          if (dx < 0) {
            (cb.onSwipeLeft ?? (() => rendition.next()))();
          } else {
            (cb.onSwipeRight ?? (() => rendition.prev()))();
          }
          swipeStart = null;
          return;
        }

        const now = Date.now();
        if (
          lastTap &&
          now - lastTap.t < DOUBLE_TAP_WINDOW_MS &&
          Math.hypot(t.clientX - lastTap.x, t.clientY - lastTap.y) < DOUBLE_TAP_DISTANCE_PX
        ) {
          applyScale(doc, 1.0);
          currentScale = 1.0;
          lastTap = null;
        } else {
          lastTap = { t: now, x: t.clientX, y: t.clientY };
        }
      }

      swipeStart = null;
    };

    const onTouchCancel = () => {
      longPressStart = null;
      swipeStart = null;
      pinchInitialDist = null;
    };

    // Desktop: fire onLongPress on mouseup when a non-empty selection exists.
    const onMouseUp = (e: MouseEvent) => {
      setTimeout(() => {
        const sel = contents.window.getSelection?.();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
        const text = sel.toString().trim();
        if (text.length === 0) return;
        cb.onLongPress?.({
          clientX: e.clientX,
          clientY: e.clientY,
          target: e.target,
        });
      }, 50);
    };

    // Touch: iOS creates native selection on long-press then fires
    // touchcancel (not touchend/mouseup). Watch selectionchange and
    // dispatch when a stable non-empty selection exists.
    let selDebounce: ReturnType<typeof setTimeout> | null = null;
    const onSelectionChange = () => {
      if (selDebounce) clearTimeout(selDebounce);
      selDebounce = setTimeout(() => {
        const sel = contents.window.getSelection?.();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
        const text = sel.toString().trim();
        if (text.length === 0) return;
        const range = sel.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        cb.onLongPress?.({
          clientX: rect.left + rect.width / 2,
          clientY: rect.top,
          target: range.startContainer,
        });
      }, 300);
    };

    doc.addEventListener('touchstart', onTouchStart, { passive: false });
    doc.addEventListener('touchmove', onTouchMove, { passive: false });
    doc.addEventListener('touchend', onTouchEnd, { passive: false });
    doc.addEventListener('touchcancel', onTouchCancel);
    doc.addEventListener('mouseup', onMouseUp);
    doc.addEventListener('selectionchange', onSelectionChange);
  };
}

function distance(a: Touch, b: Touch): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function applyScale(doc: Document, scale: number): void {
  doc.body.style.transformOrigin = 'center top';
  doc.body.style.transform = `scale(${scale.toFixed(3)})`;
}

/**
 * Suppress the native iOS callout and tap-highlight while keeping
 * text selection enabled so the user can select + highlight.
 */
function injectReaderStyles(doc: Document): void {
  const STYLE_ID = 'learnleaf-reader-styles';
  if (doc.getElementById(STYLE_ID)) return;

  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    html, body, body * {
      -webkit-touch-callout: none !important;
      -webkit-tap-highlight-color: transparent;
    }
  `;
  doc.head.appendChild(style);

  doc.addEventListener('contextmenu', (e) => e.preventDefault());
}
